import { useEffect, useMemo } from 'react';
import {
  SCENARIO_MODEL_FLASH,
  SCENARIO_MODEL_FLASH_MID,
  SCENARIO_MODEL_PRO,
  SCENARIO_MODEL_ARGON,
  SCENARIO_MODEL_GPT6_ASTRA,
  SCENARIO_MODEL_GPT6_SOL,
  SCENARIO_MODEL_GPT6_LUNA,
  SCENARIO_MODEL_CLAUDE_SONNET,
  SCENARIO_MODEL_CLAUDE_OPUS,
  SCENARIO_MODEL_CLAUDE_FABLE,
  SCENARIO_MODEL_RANDOM,
} from '../../state/useGenerationState';
import { SCENARIO_MODEL_CATALOG } from '../scenarioModelCatalog';

/** useCreditCalculations が返す scenarioModelCredits と同じ形 */
export interface ScenarioModelCredits {
  flash: number | null;
  flashMid: number | null;
  pro: number | null;
  argon: number | null;
  gpt6astra: number | null;
  gpt6sol: number | null;
  gpt6luna: number | null;
  claudeSonnet: number | null;
  claudeOpus: number | null;
  claudeFable: number | null;
}

export interface ScenarioModelOption {
  value: string;
  label: string;
  icon?: string;
  sublabel?: string;
  badge?: string | number;
  divider?: boolean;
}

export interface ScenarioModelDef {
  value: string;
  rate: number | null;
  icon: string;
  label: string;
  sublabel: string;
  /** モデルファミリー名 ('Claude' / 'GPT' / 'Gemini')。ドロップダウンのグループ見出しに使う */
  family: string;
  /** 品質ティア ('高品質' / '標準' / 'スピード')。ドロップダウンのフィルタに使う */
  tier: string;
}

/**
 * シナリオ生成モデルの選択肢構築と選択中モデルのレート解決。
 *
 * - credit_rate_config で is_active=false のモデルは rate が null で返るため、選択肢から除外する
 * - モデルプロバイダ (claude / gpt / gemini / ...) の境目には自動で区切り線を差し込む
 * - 選択中モデルがレート無効化で消滅した場合は先頭 (通常 Flash) にフォールバック
 */
export function useScenarioModelOptions({
  scenarioModel,
  setScenarioModel,
  scenarioModelCredits,
  scenarioCount,
}: {
  scenarioModel: string;
  setScenarioModel: (model: string) => void;
  scenarioModelCredits: ScenarioModelCredits;
  /** 「ランダム」枠の総消費レンジ算出に使う (実際に使われるモデル数 = min(本数, 有効モデル数))。 */
  scenarioCount: number;
}) {
  const selectedScenarioModelRate = useMemo<number | null>(() => {
    // ランダムは単一レートで表せない (複数モデル混在)。総額は randomCreditText 側でレンジ表示する。
    if (scenarioModel === SCENARIO_MODEL_RANDOM) return null;
    switch (scenarioModel) {
      case SCENARIO_MODEL_GPT6_ASTRA: return scenarioModelCredits.gpt6astra;
      case SCENARIO_MODEL_GPT6_SOL: return scenarioModelCredits.gpt6sol;
      case SCENARIO_MODEL_GPT6_LUNA: return scenarioModelCredits.gpt6luna;
      case SCENARIO_MODEL_CLAUDE_FABLE: return scenarioModelCredits.claudeFable;
      case SCENARIO_MODEL_CLAUDE_OPUS: return scenarioModelCredits.claudeOpus;
      case SCENARIO_MODEL_CLAUDE_SONNET: return scenarioModelCredits.claudeSonnet;
      case SCENARIO_MODEL_ARGON: return scenarioModelCredits.argon;
      case SCENARIO_MODEL_PRO: return scenarioModelCredits.pro;
      case SCENARIO_MODEL_FLASH_MID: return scenarioModelCredits.flashMid;
      default: return scenarioModelCredits.flash;
    }
  }, [scenarioModel, scenarioModelCredits]);

  // モデル選択肢は SCENARIO_MODEL_CATALOG (単一マスタ) から導出する。
  // レートのみ credit_rate_config 由来 (scenarioModelCredits) のため value → key で引く。
  const scenarioModelDefs = useMemo<ScenarioModelDef[]>(() => {
    const rateByValue: Record<string, number | null> = {
      [SCENARIO_MODEL_CLAUDE_SONNET]: scenarioModelCredits.claudeSonnet,
      [SCENARIO_MODEL_CLAUDE_OPUS]: scenarioModelCredits.claudeOpus,
      [SCENARIO_MODEL_CLAUDE_FABLE]: scenarioModelCredits.claudeFable,
      [SCENARIO_MODEL_GPT6_ASTRA]: scenarioModelCredits.gpt6astra,
      [SCENARIO_MODEL_GPT6_SOL]: scenarioModelCredits.gpt6sol,
      [SCENARIO_MODEL_GPT6_LUNA]: scenarioModelCredits.gpt6luna,
      [SCENARIO_MODEL_FLASH]: scenarioModelCredits.flash,
      [SCENARIO_MODEL_FLASH_MID]: scenarioModelCredits.flashMid,
      [SCENARIO_MODEL_PRO]: scenarioModelCredits.pro,
      [SCENARIO_MODEL_ARGON]: scenarioModelCredits.argon,
    };
    return SCENARIO_MODEL_CATALOG.map((spec) => ({
      value: spec.value,
      rate: rateByValue[spec.value] ?? null,
      icon: spec.icon,
      label: spec.tier,
      sublabel: spec.modelName,
      family: spec.family,
      tier: spec.tier,
    }));
  }, [scenarioModelCredits]);

  // 有効モデルの per-scenario レート (昇順)。ランダム枠のバッジ / 総額レンジ算出に使う。
  const activeScenarioRates = useMemo<number[]>(
    () => scenarioModelDefs
      .filter((d) => d.rate !== null)
      .map((d) => d.rate as number)
      .sort((a, b) => a - b),
    [scenarioModelDefs],
  );

  // ドロップダウンの「ランダム」バッジ: 1 本あたりの消費レンジ (最小〜最大、他モデルと単位を揃える)。
  const randomRateBadge = useMemo<string | undefined>(() => {
    if (activeScenarioRates.length === 0) return undefined;
    const min = activeScenarioRates[0];
    const max = activeScenarioRates[activeScenarioRates.length - 1];
    return min === max ? `${min}` : `${min}~${max}`;
  }, [activeScenarioRates]);

  // 「ランダム」選択時の総消費レンジ (数値)。使用モデル数 = min(本数, 有効モデル数)。
  // 最小 = 安い k モデルの合計、最大 = 高い k モデルの合計。minSingle = 最安 1 モデル。
  // minSingle は「1 案も作れない＝必ず全失敗」になる残高ゲートの閾値に使う。
  const randomCreditRange = useMemo<{ minTotal: number; maxTotal: number; minSingle: number } | null>(() => {
    const rates = activeScenarioRates;
    if (rates.length === 0) return null;
    const k = Math.max(1, Math.min(scenarioCount, rates.length));
    const minTotal = rates.slice(0, k).reduce((s, r) => s + r, 0);
    const maxTotal = rates.slice(rates.length - k).reduce((s, r) => s + r, 0);
    return { minTotal, maxTotal, minSingle: rates[0] };
  }, [activeScenarioRates, scenarioCount]);

  const randomCreditText = useMemo<string | null>(() => {
    if (!randomCreditRange) return null;
    const { minTotal, maxTotal } = randomCreditRange;
    return minTotal === maxTotal ? `${minTotal}` : `${minTotal}~${maxTotal}`;
  }, [randomCreditRange]);

  const scenarioModelOptions = useMemo<ScenarioModelOption[]>(() => {
    const active = scenarioModelDefs.filter((d) => d.rate !== null);
    const providerOf = (v: string) => v.split('-')[0]; // 'gemini' / 'gpt' / 'claude' ...
    const out: ScenarioModelOption[] = [];
    // 先頭に「ランダム」枠を置く (有効モデルが 1 つ以上あるときのみ)。既定モデルでもある。
    if (active.length > 0) {
      out.push({
        value: SCENARIO_MODEL_RANDOM,
        label: 'ランダム',
        sublabel: '各モデルから1案',
        badge: randomRateBadge,
      });
      out.push({ value: '__divider-random', label: '', divider: true });
    }
    active.forEach((d, i) => {
      if (i > 0 && providerOf(d.value) !== providerOf(active[i - 1].value)) {
        out.push({ value: `__divider-${i}`, label: '', divider: true });
      }
      out.push({ value: d.value, label: d.label, icon: d.icon, sublabel: d.sublabel, badge: d.rate as number });
    });
    return out;
  }, [scenarioModelDefs, randomRateBadge]);

  const currentScenarioModelDef = useMemo(
    () => scenarioModelDefs.find((d) => d.value === scenarioModel) ?? null,
    [scenarioModelDefs, scenarioModel],
  );

  // 選択中モデルがレート無効化で消滅した場合は、先頭 (通常 Flash) にフォールバック。
  useEffect(() => {
    if (scenarioModelOptions.length === 0) return;
    if (!scenarioModelOptions.some((opt) => opt.value === scenarioModel)) {
      setScenarioModel(scenarioModelOptions[0].value);
    }
  }, [scenarioModelOptions, scenarioModel, setScenarioModel]);

  return {
    selectedScenarioModelRate,
    scenarioModelOptions,
    scenarioModelDefs,
    currentScenarioModelDef,
    randomCreditText,
    randomCreditRange,
    randomRateBadge,
  };
}
